import React from 'react';
import { useTranslation } from 'react-i18next';
import { Button, IconButton, Tab, Tabs, Theme, ThemeOptions, Grid as GridMui } from '@mui/material';
import { makeStyles } from '@mui/styles';
import TabLabelIcon from '@/components/TabLabel/TabLabelIcon';
import TabsScroller from '@/components/TabsComponent/TabsScroller';
import { statusSubCampaign } from '@/helpers/constants';
import styles from './Feedback.module.scss';

type TabLabelWithConfig = {
  label: string;
  deleteDisabled?: boolean;
  invalid?: boolean;
  status?: string;
};

interface TabsCampaignsSubCRProps {
  tabsLabelsConfig: TabLabelWithConfig[];
  onAddNewTab?: () => void;
  onDeleteTab?: (index: number) => void;
  readOnly?: boolean;
  setTabSelected: (index: number) => void;
  crSelected: number;
  maxTabs?: number;
  noAddButton?: boolean;
  cutomTabLabel?: string;
  originalCampaignsCount?: number;
}

const useStyles = makeStyles((theme: ThemeOptions & Theme) => ({
  TabRoot: { fontSize: theme.typography.pxToRem(16), opacity: 0.9, fontWeight: 700, minHeight: '50px', border: '1px solid grey', margin: '0px 0px 0px 0px', borderRadius: '5px', backgroundColor: theme.palette.grey[200], minWidth: '250px', display: 'flex', justifyContent: 'space-between', borderBottomLeftRadius: '0px', borderBottomRightRadius: '0px', /* overflow: 'visible'  overflowX: 'clip', */ overflow: 'visible', marginTop: '5px' },
  TabRootSelected: { backgroundColor: 'unset' },
  AddCRTab: { fontSize: theme.typography.pxToRem(16), opacity: 0.9, fontWeight: 700 },
  TabsContainer: { gap: '5px' },
  Indicator: { height: '1px', backgroundColor: 'white', marginLeft: '1px' },
  ScrollButtons: { border: '1px solid grey', marginTop: '5px', height: '50px' },
}));

const TabsCampaignsSubCR: React.FC<TabsCampaignsSubCRProps> = (props) => {
  const { tabsLabelsConfig, onAddNewTab = () => null, onDeleteTab = () => null, readOnly = false, setTabSelected, crSelected, maxTabs = 10,
    noAddButton = false, cutomTabLabel, originalCampaignsCount = 0  } = props;
  const classes = useStyles();
  const { t } = useTranslation();
  const maxTabsReached = tabsLabelsConfig.length >= maxTabs;
  const tabLabel = cutomTabLabel || t('feedback.campaignNumber');

  const onRemoveTab = (ev, index: number) => {
    ev.stopPropagation();
    onDeleteTab(index);
  };

  const isCampaignAdded = (index: number): boolean => index >= originalCampaignsCount;

  const invalidTabs = tabsLabelsConfig.filter((tab) => tab?.invalid).map((tab, index) => ({ ...tab, position: index }));

  return (
    <GridMui
      container
      alignItems="center"
    >
      <GridMui
        item
        xs={(maxTabsReached || readOnly || noAddButton) ? 12 : 11}
        style={{ flex: 'none', width: 'auto' }}
      >
        <Tabs
          // indicatorColor="primary"
          value={crSelected}
          variant="scrollable"
          scrollButtons
          ScrollButtonComponent={invalidTabs.length > 0
            ? (nativeProps) => <TabsScroller {...nativeProps} invalidTabs={invalidTabs} containerId="subcampaign-feedback" />
            : undefined}
          orientation="horizontal"
          allowScrollButtonsMobile
          onChange={(ev, value) => setTabSelected(value)}
          classes={{ flexContainer: classes.TabsContainer, indicator: classes.Indicator, scrollButtons: classes.ScrollButtons }}
        >
          {tabsLabelsConfig.map((tab, index) => (
            <Tab
              key={index}
              label={(
                <span className={styles.TabLabel}>
                  {tab?.invalid && <TabLabelIcon />}{tab?.label || 'N/A'}
                </span>
              )}
              value={index}
              classes={{ root: `${classes.TabRoot} ${index === crSelected ? classes.TabRootSelected : ''}` }}
              icon={(
                <>
                  {(((!readOnly || tab?.deleteDisabled) && tab.status === statusSubCampaign.IN_ANALYSIS) || isCampaignAdded(index)) && (
                    <div
                      onClick={(ev) => {
                        onRemoveTab(ev, index);
                      }}
                      style={{ padding: '0px 5px', fontWeight: 400 }}
                      id={`delete-tab-${index}`}
                      data-testid={`delete-tab-${index}`}
                    >
                      <i className="di icon-schliessen" />
                    </div>
                  )}
                  <span className={styles.CRNumberTabLabel}>{tabLabel}</span>
                </>
              )}
              iconPosition="end"
            />
          ))}
        </Tabs>
      </GridMui>
      {(!readOnly && !noAddButton) && (
        <GridMui
          item
          xs={1}
        >
          {!maxTabsReached && (
            <Button
              classes={{ root: classes.AddCRTab }}
              color="primary"
              onClick={onAddNewTab}
              id="add-tab"
              data-testid="add-tab"
            >
              <IconButton
                sx={{
                  '&:hover': {
                    backgroundColor: (theme) => theme.palette.grey[200],
                  },
                  backgroundColor: (theme) => theme.palette.grey[400],
                }}
              >
                <i className="di icon-plus-hinzufuegen-klein" /* style={{ color: 'white' }} */ />
              </IconButton>
            </Button>
          )}
        </GridMui>
      )}
    </GridMui>
  );
};

export default TabsCampaignsSubCR;
